import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { TreasuryDrainStage } from '../../enums/treasury-drain-stage.enum';

/**
 * What core answers about a rolled-back organization's drain (SD-10965).
 *
 * ⚠️ THE FIGURES ARE A RECORD, NOT A LIVE READING. Core does not call Stripe to answer this, so
 * every amount here is what the drain last OBSERVED. `balanceRefreshedAt` is how old that
 * observation is, and the screen must show it.
 */
export interface TreasuryDrainStateResponse {
    organizationId: number;
    stage: TreasuryDrainStage;
    availableCashCents: number;
    pendingCents: number;

    /** When the two figures above were observed. NULL means no observation exists at all. */
    balanceRefreshedAt: string | null;

    /**
     * ⚠️ THE FIELD THAT STOPS THE SCREEN LYING. `availableCashCents` defaults to 0, so an
     * organization nobody has looked at yet is indistinguishable from one whose balance is empty
     * — and rendering the first as "$0.00 available" tells an association its money is gone when
     * the truth is that we have not checked.
     */
    hasBalanceObservation: boolean;

    lastTransactionAt: string | null;
    drainCompletedAt: string | null;

    /**
     * Card authorizations still outstanding. A COUNT, not a boolean.
     *
     * ⚠️ ANY NON-ZERO VALUE HOLDS CLOSE OPEN even after the quiet period has fully elapsed. An
     * approved authorization is not a transaction and never moves `lastTransactionAt`, so the
     * clock can run out underneath one.
     */
    pendingAuthorizationCount: number;

    /** The earliest the account may close. NULL before the drain completes. */
    closeEligibleAt: string | null;

    /** Where the money is going, as core names it. Null when no destination is set yet. */
    destinationName: string | null;

    destinationUnifiedBankAccountId: number | null;

    /**
     * Last four digits of the destination account.
     *
     * ⚠️ CAN BE AN EMPTY STRING, not just null — rows Stripe mirrors in without an account number
     * carry "". The template must check it rather than assume four digits are there.
     */
    destinationLastFour: string | null;

    /** Stripe's own words when a drain transfer was refused. */
    lastError: string | null;
}

/**
 * The drain view's data (SD-10965).
 *
 * After Freeze, the Financial Accounts page is THE ONE PLACE the Treasury account is still
 * visible, so an association and support can watch the money leave. This service is what it reads.
 *
 * ⚠️ FAILS TO `NONE`, exactly like {@link TreasuryConversionStageService} beside it, and for the
 * same reason: every surface keyed on this is ADDITIVE. A failed fetch must degrade to the screen
 * an organization sees today, never to a half-rendered drain panel. The opposite default would let
 * one failed request tell an association its account was draining when it was not.
 *
 * ⚠️ NOT CACHED PER ORGANIZATION, unlike the stage service, and that is deliberate rather than an
 * oversight. The stage changes a handful of times in a conversion's life; these figures change
 * every time the drain runs, and this is the screen somebody refreshes BECAUSE they want a newer
 * number. A cache here would answer a worried admin with the figure they were already looking at.
 */
@Injectable({ providedIn: 'root' })
export class TreasuryDrainStateService {
    constructor(private readonly _http: ApplicationHttpClient) {}

    /**
     * ⚠️ `MakeLegFiCoreUrl` PREPENDS THE CORE API BASE. Without it the path resolves against the
     * PAGE origin and 404s, and the `catchError` below turns that into `NONE` — which is
     * indistinguishable from "this organization is not draining", so the panel would never appear
     * for anybody and nothing would report why. Every sibling call in this route family wraps the
     * path the same way (review F-cf2f).
     */
    fetch(organizationId: number): Observable<TreasuryDrainStateResponse> {
        return this._http
            .get<TreasuryDrainStateResponse>(
                Routes.MakeLegFiCoreUrl(Routes.TreasuryModule.DrainState(organizationId)),
            )
            .pipe(
                map((response: TreasuryDrainStateResponse) => response ?? this.none(organizationId)),
                catchError(() => of(this.none(organizationId))),
            );
    }

    /**
     * The "nothing to show" answer.
     *
     * ⚠️ `hasBalanceObservation` IS FALSE HERE, not merely the cents being zero. A fallback
     * claiming an observation of $0.00 would render a balance panel over a failed request.
     */
    private none(organizationId: number): TreasuryDrainStateResponse {
        return {
            organizationId,
            stage: TreasuryDrainStage.NONE,
            availableCashCents: 0,
            pendingCents: 0,
            balanceRefreshedAt: null,
            hasBalanceObservation: false,
            lastTransactionAt: null,
            drainCompletedAt: null,
            pendingAuthorizationCount: 0,
            closeEligibleAt: null,
            destinationName: null,
            destinationUnifiedBankAccountId: null,
            destinationLastFour: null,
            lastError: null,
        };
    }
}
